import {
  StateId, StateEntry, SceneDefinition, DetectedScene, ContextConfig,
  PauseSnapshot, PauseSpec, PauseRange, Mode, Region,
} from '../types';
import { SCENE_COOLDOWN } from '../config/constants';

export interface SensorEvidence {
  gazeZ: number;
  keyboardZ: number;
  voiceSDR: number;
  stylusZ: number;
  bodyZ: number;
  /** Per-modality pause durations. v6+ rules can constrain on these. */
  pauses?: PauseSnapshot;
}

// ═══════════════════════════════════════════════════════════
// Scene Detection v6 — v5 + Pause-as-1st-class signal
//                        + Mode/Region filters
// ═══════════════════════════════════════════════════════════

interface ObservationWindow {
  observeStates: StateId[];
  actionStates: StateId[];
  windowMs: number;
  minObserveDurationMs: number;
  minObserveCount: number;
  /** If true, ALL observeStates must appear (not just any). For multi-type rules. */
  requireAllObserveTypes?: boolean;
  maxObserveToActionGapMs: number;
  /** Action must be among the N most recent states. Default: 3 */
  actionRecencyLimit?: number;
}

interface SceneRule {
  id: string;
  severity: 'CRITICAL' | 'WARNING' | 'NORMAL';
  label: string;
  desc: string;
  icon: string;
  window: ObservationWindow;
  sensorChannels: ('gaze' | 'keyboard' | 'voice' | 'stylus')[];
  baseConfidence: number;
  /**
   * Per-modality pause requirement. If set, the rule will only fire when
   * the current pause snapshot satisfies this spec. Can be combined with
   * an action pattern or used standalone (with empty observe/action states).
   */
  pauseSpec?: PauseSpec;
  /** If set, rule applies only in these modes. Absent = all modes. */
  modes?: Mode[];
  /** If set, rule applies only in these regions. Absent = all regions. */
  regions?: Region[];
}

// ─── Helpers ─────────────────────────────────────────────────

/** Returns true iff `value` lies in [range.minMs, range.maxMs]. */
function pauseInRange(value: number, range: PauseRange): boolean {
  const min = range.minMs ?? 0;
  const max = range.maxMs ?? Number.POSITIVE_INFINITY;
  return value >= min && value <= max;
}

/** Evaluate a PauseSpec against a PauseSnapshot. */
function checkPauseSpec(spec: PauseSpec, pauses: PauseSnapshot): boolean {
  const checks: boolean[] = [];
  if (spec.gaze)       checks.push(pauseInRange(pauses.gazeMs,       spec.gaze));
  if (spec.keyboard)   checks.push(pauseInRange(pauses.keyboardMs,   spec.keyboard));
  if (spec.stylus)     checks.push(pauseInRange(pauses.stylusMs,     spec.stylus));
  if (spec.voice)      checks.push(pauseInRange(pauses.voiceMs,      spec.voice));
  if (spec.multimodal) checks.push(pauseInRange(pauses.multimodalMs, spec.multimodal));
  if (checks.length === 0) return true;
  return spec.combine === 'any' ? checks.some(Boolean) : checks.every(Boolean);
}

// ═══════════════════════════════════════════════════════════
// Scene Rules v5
// ═══════════════════════════════════════════════════════════

const SCENE_RULES: SceneRule[] = [
  // ── 옆보기 관련 ──
  {
    id: 'side_copy_burst',
    severity: 'CRITICAL',
    label: '옆을 보다가 작성 시작',
    desc: '옆을 보다가 돌아와서 답을 쓰기 시작',
    icon: '🔴',
    window: {
      observeStates: ['LOOKING_SIDE'],
      actionStates: ['BURST_TYPING'],
      windowMs: 20000,
      minObserveDurationMs: 300,
      minObserveCount: 1,
      maxObserveToActionGapMs: 8000,
      actionRecencyLimit: 3,
    },
    sensorChannels: ['gaze', 'keyboard'],
    baseConfidence: 0.55,
  },
  {
    id: 'side_copy_write',
    severity: 'WARNING',
    label: '옆 보고 작성',
    desc: '옆을 보다가 작성 시작 (키보드/필기)',
    icon: '🟡',
    window: {
      observeStates: ['LOOKING_SIDE'],
      actionStates: ['WRITING_NORMAL', 'BURST_TYPING'],
      windowMs: 15000,
      minObserveDurationMs: 400,
      minObserveCount: 1,
      maxObserveToActionGapMs: 6000,
      actionRecencyLimit: 3,
    },
    sensorChannels: ['gaze', 'keyboard'],
    baseConfidence: 0.4,
  },
  {
    id: 'prolonged_side_stare',
    severity: 'WARNING',
    label: '장시간 옆 응시',
    desc: '옆을 5초 이상 지속 응시',
    icon: '🟡',
    window: {
      observeStates: ['LOOKING_SIDE'],
      actionStates: [],
      windowMs: 15000,
      minObserveDurationMs: 5000, // 5초 이상 연속
      minObserveCount: 1,
      maxObserveToActionGapMs: 0,
    },
    sensorChannels: ['gaze'],
    baseConfidence: 0.4,
  },
  {
    id: 'repeated_glance',
    severity: 'WARNING',
    label: '옆 반복 응시',
    desc: '옆을 3회 이상 반복 확인',
    icon: '🟡',
    window: {
      observeStates: ['LOOKING_SIDE'],
      actionStates: [],
      windowMs: 20000,
      minObserveDurationMs: 500,   // 총 0.5초 이상
      minObserveCount: 3,          // 3회 이상이어야 (2회는 자연스러울 수 있음)
      maxObserveToActionGapMs: 0,
    },
    sensorChannels: ['gaze'],
    baseConfidence: 0.35,
  },
  {
    id: 'repeated_glance_then_write',
    severity: 'CRITICAL',
    label: '옆 반복 응시 후 작성',
    desc: '옆을 여러 번 확인한 뒤 작성',
    icon: '🔴',
    window: {
      observeStates: ['LOOKING_SIDE'],
      actionStates: ['WRITING_NORMAL', 'BURST_TYPING'],
      windowMs: 25000,
      minObserveDurationMs: 200,
      minObserveCount: 2,
      maxObserveToActionGapMs: 10000,
      actionRecencyLimit: 4,
    },
    sensorChannels: ['gaze', 'keyboard'],
    baseConfidence: 0.55,
  },

  // ── 아래보기 관련 ──
  {
    id: 'memo_ref_burst',
    severity: 'WARNING',
    label: '아래를 보다가 작성 시작',
    desc: '아래를 보다가 답을 쓰기 시작',
    icon: '🟡',
    window: {
      observeStates: ['LOOKING_DOWN'],
      actionStates: ['BURST_TYPING'],
      windowMs: 15000,
      minObserveDurationMs: 400,
      minObserveCount: 1,
      maxObserveToActionGapMs: 6000,
      actionRecencyLimit: 3,
    },
    sensorChannels: ['gaze', 'keyboard'],
    baseConfidence: 0.45,
  },
  {
    id: 'memo_ref_write',
    severity: 'WARNING',
    label: '아래 보고 작성',
    desc: '아래를 보다가 작성 시작',
    icon: '🟡',
    window: {
      observeStates: ['LOOKING_DOWN'],
      actionStates: ['WRITING_NORMAL', 'BURST_TYPING'],
      windowMs: 15000,
      minObserveDurationMs: 500,
      minObserveCount: 1,
      maxObserveToActionGapMs: 6000,
      actionRecencyLimit: 3,
    },
    sensorChannels: ['gaze'],
    baseConfidence: 0.35,
  },
  {
    id: 'watch_ref',
    severity: 'CRITICAL',
    label: '아래 반복 주시 후 작성',
    desc: '아래를 반복 주시 후 작성',
    icon: '🔴',
    window: {
      observeStates: ['LOOKING_DOWN'],
      actionStates: ['WRITING_NORMAL', 'BURST_TYPING'],
      windowMs: 20000,
      minObserveDurationMs: 200,
      minObserveCount: 2,
      maxObserveToActionGapMs: 8000,
      actionRecencyLimit: 4,
    },
    sensorChannels: ['gaze', 'keyboard'],
    baseConfidence: 0.55,
  },

  // ── 음성 관련 ──
  {
    id: 'answer_heard',
    severity: 'CRITICAL',
    label: '답 듣고 작성',
    desc: '음성 감지 후 작성',
    icon: '🔴',
    window: {
      observeStates: ['LISTENING'],
      actionStates: ['BURST_TYPING', 'WRITING_NORMAL'],
      windowMs: 15000,
      minObserveDurationMs: 500,
      minObserveCount: 1,
      maxObserveToActionGapMs: 8000,
      actionRecencyLimit: 3,
    },
    sensorChannels: ['voice', 'keyboard'],
    baseConfidence: 0.55,
  },
  {
    id: 'remote_assist',
    severity: 'CRITICAL',
    label: '음성·시선 이동 후 작성',
    desc: '음성 + 시선 이동 + 작성',
    icon: '🔴',
    window: {
      observeStates: ['LISTENING', 'LOOKING_SIDE'],
      actionStates: ['BURST_TYPING', 'WRITING_NORMAL'],
      windowMs: 20000,
      minObserveDurationMs: 300,
      minObserveCount: 2,
      requireAllObserveTypes: true, // 음성 AND 시선 모두 필요
      maxObserveToActionGapMs: 10000,
      actionRecencyLimit: 4,
    },
    sensorChannels: ['voice', 'gaze', 'keyboard'],
    baseConfidence: 0.6,
  },

  // ── 카메라 ──
  {
    id: 'camera_block',
    severity: 'CRITICAL',
    label: '갑작스런 시선 이탈 + 입력 정지',
    desc: '시선 이탈 + 입력 정지',
    icon: '🔴',
    window: {
      observeStates: ['GAZING_OUT'],
      actionStates: ['IDLE'],
      windowMs: 10000,
      minObserveDurationMs: 2000,
      minObserveCount: 1,
      maxObserveToActionGapMs: 3000,
      actionRecencyLimit: 2,
    },
    sensorChannels: ['gaze'],
    baseConfidence: 0.6,
  },

  // ── 정상 시나리오 ──
  {
    id: 'air_thinking',
    severity: 'NORMAL',
    label: '생각 후 작성',
    desc: '위를 보며 생각한 뒤 작성',
    icon: '🟢',
    window: {
      observeStates: ['THINKING_UP'],
      actionStates: ['WRITING_NORMAL'],
      windowMs: 15000,
      minObserveDurationMs: 500,
      minObserveCount: 1,
      maxObserveToActionGapMs: 8000,
      actionRecencyLimit: 3,
    },
    sensorChannels: [],
    baseConfidence: 0.5,
  },
  {
    id: 'rest_resume',
    severity: 'NORMAL',
    label: '잠깐 쉬고 재개',
    desc: '잠시 쉰 뒤 작성 재개',
    icon: '🟢',
    window: {
      observeStates: ['GAZING_OUT'],
      actionStates: ['WRITING_NORMAL'],
      windowMs: 20000,
      minObserveDurationMs: 1000,
      minObserveCount: 1,
      maxObserveToActionGapMs: 10000,
      actionRecencyLimit: 3,
    },
    sensorChannels: [],
    baseConfidence: 0.45,
  },
  {
    id: 'steady_focus',
    severity: 'NORMAL',
    label: '안정적 집중',
    desc: '정면 집중 + 꾸준한 입력',
    icon: '🟢',
    window: {
      observeStates: ['FOCUSED'],
      actionStates: ['WRITING_NORMAL'],
      windowMs: 15000,
      minObserveDurationMs: 5000,
      minObserveCount: 1,
      maxObserveToActionGapMs: 5000,
      actionRecencyLimit: 3,
    },
    sensorChannels: [],
    baseConfidence: 0.5,
  },

  // ── 행동 패턴 ──
  {
    id: 'give_up_idle',
    severity: 'WARNING',
    label: '입력 없이 먼 곳 응시 반복',
    desc: '먼 곳 주시 반복, 입력 없음',
    icon: '🟠',
    window: {
      observeStates: ['GAZING_OUT'],
      actionStates: [],
      windowMs: 30000,
      minObserveDurationMs: 2000,
      minObserveCount: 3,
      maxObserveToActionGapMs: 0,
    },
    sensorChannels: [],
    baseConfidence: 0.4,
  },
  {
    id: 'panic_typing',
    severity: 'WARNING',
    label: '빠른 입력 중 옆 확인',
    desc: '빠른 입력 중 옆을 확인',
    icon: '🟠',
    window: {
      observeStates: ['LOOKING_SIDE'],
      actionStates: ['BURST_TYPING'],
      windowMs: 10000,
      minObserveDurationMs: 200,
      minObserveCount: 1,
      maxObserveToActionGapMs: 3000,
      actionRecencyLimit: 2,
    },
    sensorChannels: ['gaze', 'keyboard'],
    baseConfidence: 0.4,
  },

  // ═══════════════════════════════════════════════════════════
  // PAUSE-BASED RULES (v6)
  // Pause = 1st-class signal. Same pause length means different things
  // depending on which modalities are paused and what state is active.
  // ═══════════════════════════════════════════════════════════

  // ── Inattention TIERS: "Pause의 길이"가 의미를 결정 ──
  // mild(15-30s) / standard(30-60s) / severe(60s+) — 같은 패턴, 다른 강도.
  // 멀티윈도우 tiers 본격 도입 전, 명시적 룰 분리로 표현.

  // ── 약한 집중 이탈 (15-30s) ──
  {
    id: 'mild_inattention',
    severity: 'WARNING',
    label: '약한 집중 이탈',
    desc: '15초 이상 모든 채널에서 활동 없음',
    icon: '🟡',
    window: {
      observeStates: [],
      actionStates: [],
      windowMs: 30000,
      minObserveDurationMs: 0,
      minObserveCount: 0,
      maxObserveToActionGapMs: 0,
    },
    pauseSpec: {
      multimodal: { minMs: 15000, maxMs: 30000 },
    },
    sensorChannels: [],
    baseConfidence: 0.45,
  },

  // ── 표준 집중 이탈 (30-60s) ──
  {
    id: 'inattention_long',
    severity: 'WARNING',
    label: '30초 이상 활동 없음',
    desc: '30초 이상 모든 채널에서 활동 없음',
    icon: '🟠',
    window: {
      observeStates: [],
      actionStates: [],
      windowMs: 60000,
      minObserveDurationMs: 0,
      minObserveCount: 0,
      maxObserveToActionGapMs: 0,
    },
    pauseSpec: {
      multimodal: { minMs: 30000, maxMs: 60000 },
    },
    sensorChannels: [],
    baseConfidence: 0.55,
  },

  // ── 심각한 집중 이탈 (60s+) ──
  {
    id: 'severe_inattention',
    severity: 'CRITICAL',
    label: '60초 이상 활동 없음',
    desc: '60초 이상 모든 채널 무활동',
    icon: '🔴',
    window: {
      observeStates: [],
      actionStates: [],
      windowMs: 90000,
      minObserveDurationMs: 0,
      minObserveCount: 0,
      maxObserveToActionGapMs: 0,
    },
    pauseSpec: {
      multimodal: { minMs: 60000 },
    },
    sensorChannels: [],
    baseConfidence: 0.7,
  },

  // ── 고민중 (Deliberation): 작성은 멈췄지만 시선은 살아있음 ──
  // 키보드/스타일러스 5s+ 정지 + gaze는 최근에 움직임(≤4s) + state는 사고/집중.
  // 이건 정상 신호 (NORMAL) — 학생이 문제를 깊게 고민하는 중.
  {
    id: 'deliberation_pause',
    severity: 'NORMAL',
    label: '깊은 고민',
    desc: '작성은 멈췄지만 시선이 움직이며 사고 중',
    icon: '🟢',
    window: {
      observeStates: ['THINKING_UP', 'FOCUSED'],
      actionStates: [],
      windowMs: 30000,
      minObserveDurationMs: 3000,
      minObserveCount: 1,
      maxObserveToActionGapMs: 0,
    },
    pauseSpec: {
      keyboard: { minMs: 5000 },
      stylus:   { minMs: 5000 },
      gaze:     { maxMs: 4000 },  // 시선은 살아있어야 함
    },
    sensorChannels: [],
    baseConfidence: 0.5,
  },

  // ── 입력 정지 + 시선이 옆/아래로 반복 이동 ──
  // 키보드 10s+ 정지 상태에서 LOOKING_SIDE/LOOKING_DOWN이 누적된 상황만
  // 행동 기술. 해석 없이 기술만.
  {
    id: 'pause_side_down_repeat',
    severity: 'WARNING',
    label: '작성 정지 중 옆/아래 반복 주시',
    desc: '작성 정지 상태에서 옆/아래를 반복 확인',
    icon: '🟠',
    window: {
      observeStates: ['LOOKING_SIDE', 'LOOKING_DOWN'],
      actionStates: [],
      windowMs: 30000,
      minObserveDurationMs: 1500,
      minObserveCount: 2,
      maxObserveToActionGapMs: 0,
    },
    pauseSpec: {
      keyboard: { minMs: 10000 },
      stylus:   { minMs: 10000 },
    },
    sensorChannels: ['gaze'],
    baseConfidence: 0.5,
  },

  // ── 답 듣고 작성 (Pause-aware): 음성→침묵→급타 ──
  // LISTENING 후 키보드가 잠깐(3-10s) 멈췄다가 BURST_TYPING.
  // pause "구간"을 지정해서 즉답(0s)이나 너무 늦은 답(>10s)은 제외.
  {
    id: 'answer_heard_pause',
    severity: 'CRITICAL',
    label: '답 듣고 정리 후 작성',
    desc: '음성 감지 후 잠깐 멈췄다 답을 쓰기 시작',
    icon: '🔴',
    window: {
      observeStates: ['LISTENING'],
      actionStates: ['BURST_TYPING'],
      windowMs: 30000,
      minObserveDurationMs: 800,
      minObserveCount: 1,
      maxObserveToActionGapMs: 12000,
      actionRecencyLimit: 3,
    },
    pauseSpec: {
      // 정답이 들린 직후에는 정리하느라 잠깐 멈춤이 생긴다.
      // 너무 짧으면 즉답(부자연), 너무 길면 무관한 일시정지로 본다.
      voice:    { minMs: 1000 },           // 발화는 끝나 있어야
      keyboard: { minMs: 2000, maxMs: 10000 }, // 2-10s 짧은 정지
    },
    sensorChannels: ['voice', 'keyboard'],
    baseConfidence: 0.6,
  },

  // ── Frozen Stare: 화면을 멍하니 응시 (≠ 고민중) ──
  // gaze까지 8s+ 정지(시선이 움직이지 않음) + 키보드/스타일러스도 정지.
  // deliberation_pause는 gaze가 살아있는데, 이건 시선까지 얼어있어서 다름.
  // 시험 스트레스로 얼어버린 "deer in headlights" 상태.
  {
    id: 'frozen_stare',
    severity: 'WARNING',
    label: '화면 응시 + 시선·입력 정지',
    desc: '화면을 응시하며 시선·입력 모두 정지',
    icon: '🟠',
    window: {
      observeStates: ['FOCUSED'],
      actionStates: [],
      windowMs: 30000,
      minObserveDurationMs: 5000,
      minObserveCount: 1,
      maxObserveToActionGapMs: 0,
    },
    pauseSpec: {
      gaze:     { minMs: 8000 },
      keyboard: { minMs: 8000 },
      stylus:   { minMs: 8000 },
    },
    sensorChannels: [],
    baseConfidence: 0.45,
  },
];

// ═══════════════════════════════════════════════════════════
// SceneDetector v5
// ═══════════════════════════════════════════════════════════

export class SceneDetector {
  private lastFired: Record<string, number> = {};

  private evaluateRule(
    rule: SceneRule,
    history: StateEntry[],
    now: number,
    evidence: SensorEvidence | undefined,
    config: ContextConfig,
  ): { confidence: number } | null {
    // ── Mode/Region gating ──
    const mode = config.mode ?? 'school';
    const region = config.region ?? 'KR';
    if (rule.modes && !rule.modes.includes(mode)) return null;
    if (rule.regions && !rule.regions.includes(region)) return null;

    // ── PauseSpec gating: required if rule declares one ──
    if (rule.pauseSpec) {
      if (!evidence?.pauses) return null;
      if (!checkPauseSpec(rule.pauseSpec, evidence.pauses)) return null;
    }

    const w = rule.window;

    // ── Pause-only path: rule has no observe/action requirement ──
    // Used for "Inattention" / multimodal-pause type rules where the signal
    // IS the pause itself.
    if (w.observeStates.length === 0 && w.actionStates.length === 0) {
      if (!rule.pauseSpec || !evidence?.pauses) return null;
      // Confidence scales with how far past minMs the multimodal pause is.
      const mmMin = rule.pauseSpec.multimodal?.minMs ?? 0;
      const mm = evidence.pauses.multimodalMs;
      const overshoot = mmMin > 0 ? Math.min(1.0, (mm - mmMin) / mmMin) : 0;
      const sensorBoost = this.computeSensorBoost(rule, evidence, config);
      return {
        confidence: Math.min(1.0, rule.baseConfidence + overshoot * 0.25 + sensorBoost),
      };
    }

    const cutoff = now - w.windowMs;
    const recent = history.filter(h => h.t >= cutoff);
    if (recent.length < 1) return null;

    // ── Collect observation episodes ──
    const episodes: { start: number; end: number; duration: number; state: StateId }[] = [];
    for (let i = 0; i < recent.length; i++) {
      if (w.observeStates.includes(recent[i].state)) {
        const end = i + 1 < recent.length ? recent[i + 1].t : now;
        episodes.push({
          start: recent[i].t,
          end,
          duration: end - recent[i].t,
          state: recent[i].state,
        });
      }
    }

    if (episodes.length < w.minObserveCount) return null;

    const totalDuration = episodes.reduce((s, ep) => s + ep.duration, 0);
    if (totalDuration < w.minObserveDurationMs) return null;

    // ── Check observation type diversity ──
    if (w.requireAllObserveTypes) {
      const observedTypes = new Set(episodes.map(ep => ep.state));
      for (const required of w.observeStates) {
        if (!observedTypes.has(required)) return null;
      }
    }

    // ── No action required: score from observations alone ──
    if (w.actionStates.length === 0) {
      const countScore = Math.min(1.0, episodes.length / 4);
      const durationScore = Math.min(1.0, totalDuration / 5000);
      // Bonus for longest single episode (sustained observation is more salient)
      const longestEpisode = Math.max(...episodes.map(ep => ep.duration));
      const sustainedBonus = Math.min(0.15, (longestEpisode / 5000) * 0.15);
      const sensorBoost = this.computeSensorBoost(rule, evidence, config);

      return {
        confidence: Math.min(1.0, rule.baseConfidence + countScore * 0.15 + durationScore * 0.15 + sustainedBonus + sensorBoost),
      };
    }

    // ── Find action state (must be RECENT) ──
    const recencyLimit = w.actionRecencyLimit ?? 3;
    const recentStates = recent.slice(-recencyLimit);
    let actionEntry: StateEntry | null = null;

    // Search from most recent backward
    for (let i = recentStates.length - 1; i >= 0; i--) {
      if (w.actionStates.includes(recentStates[i].state)) {
        actionEntry = recentStates[i];
        break;
      }
    }

    if (!actionEntry) return null;

    // ── Check temporal ordering: observation BEFORE action ──
    const lastObserveEnd = Math.max(...episodes.map(ep => ep.end));
    // Action must start after or near the end of observation
    if (actionEntry.t < lastObserveEnd - 2000) {
      // Action happened well before last observation — wrong order
      // (2s tolerance for overlapping states)
      return null;
    }

    // Check gap
    const gap = Math.max(0, actionEntry.t - lastObserveEnd);
    if (gap > w.maxObserveToActionGapMs) return null;

    // ── Compute confidence ──
    const countScore = Math.min(1.0, episodes.length / 3);
    const durationScore = Math.min(1.0, totalDuration / 3000);
    const proximityScore = w.maxObserveToActionGapMs > 0
      ? Math.max(0, 1.0 - (gap / w.maxObserveToActionGapMs))
      : 1.0;

    // Recency: how recently did the action happen?
    const actionAge = now - actionEntry.t;
    const recencyScore = Math.max(0, 1.0 - (actionAge / 8000));

    // Sustained observation bonus
    const longestEpisode = Math.max(...episodes.map(ep => ep.duration));
    const sustainedBonus = Math.min(0.1, (longestEpisode / 3000) * 0.1);

    // Sensor evidence
    const sensorBoost = this.computeSensorBoost(rule, evidence, config);

    const confidence = rule.baseConfidence
      + countScore * 0.10
      + durationScore * 0.10
      + proximityScore * 0.10
      + recencyScore * 0.10
      + sustainedBonus
      + sensorBoost;

    return { confidence: Math.min(1.0, confidence) };
  }

  private computeSensorBoost(rule: SceneRule, evidence: SensorEvidence | undefined, config: ContextConfig): number {
    if (!evidence || rule.sensorChannels.length === 0) return 0;

    let boost = 0;
    for (const ch of rule.sensorChannels) {
      switch (ch) {
        case 'gaze':
          if (Math.abs(evidence.gazeZ) >= config.zThresh) boost += 1.0;
          else if (Math.abs(evidence.gazeZ) >= config.zThresh * 0.5) boost += 0.4;
          break;
        case 'keyboard':
          if (Math.abs(evidence.keyboardZ) >= config.zThresh) boost += 1.0;
          else if (Math.abs(evidence.keyboardZ) >= config.zThresh * 0.5) boost += 0.4;
          break;
        case 'voice':
          if (evidence.voiceSDR >= config.voicePct) boost += 1.0;
          else if (evidence.voiceSDR >= config.voicePct * 0.5) boost += 0.4;
          break;
        case 'stylus':
          if (Math.abs(evidence.stylusZ) >= config.zThresh) boost += 0.8;
          break;
      }
    }
    return (boost / rule.sensorChannels.length) * 0.15; // Max 15%
  }

  checkScenes(
    history: StateEntry[],
    _scenes: SceneDefinition[],
    config: ContextConfig,
    evidence?: SensorEvidence,
  ): DetectedScene | null {
    const now = Date.now();

    interface Match { rule: SceneRule; confidence: number }
    const matches: Match[] = [];

    for (const rule of SCENE_RULES) {
      if (this.lastFired[rule.id] && now - this.lastFired[rule.id] < SCENE_COOLDOWN) continue;
      const result = this.evaluateRule(rule, history, now, evidence, config);
      if (result && result.confidence >= 0.3) {
        matches.push({ rule, confidence: result.confidence });
      }
    }

    if (matches.length === 0) return null;

    // Score with severity weight
    const severityWeight = { CRITICAL: 1.4, WARNING: 1.0, NORMAL: 0.8 };
    const scored = matches.map(m => ({
      ...m,
      score: m.confidence * severityWeight[m.rule.severity],
    }));
    scored.sort((a, b) => b.score - a.score);

    // Normal suppression: only if significantly more confident
    const bestCaution = scored.find(m => m.rule.severity !== 'NORMAL');
    const bestNormal = scored.find(m => m.rule.severity === 'NORMAL');

    let best: Match;
    if (bestCaution && bestNormal && bestNormal.confidence - bestCaution.confidence > 0.2) {
      best = bestNormal;
    } else {
      best = scored[0];
    }

    this.lastFired[best.rule.id] = now;

    const channels: string[] = [];
    const obs = new Set(best.rule.window.observeStates);
    const act = new Set(best.rule.window.actionStates);
    if (obs.has('LOOKING_SIDE') || obs.has('LOOKING_DOWN') || obs.has('GAZING_OUT') || obs.has('THINKING_UP'))
      channels.push('헤드포즈');
    if (act.has('BURST_TYPING') || act.has('SLOW_TYPING')) channels.push('키보드');
    if (act.has('WRITING_NORMAL')) channels.push('키보드/스타일러스');
    if (obs.has('LISTENING')) channels.push('음성');
    if (act.has('IDLE')) channels.push('입력');

    return {
      scene: {
        id: best.rule.id,
        pattern: [...best.rule.window.observeStates, ...best.rule.window.actionStates],
        severity: best.rule.severity,
        label: best.rule.label,
        desc: best.rule.desc,
        icon: best.rule.icon,
      },
      t: now,
      channels,
      confidence: Math.round(best.confidence * 100) / 100,
    };
  }
}
